import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { MenuItem } from '@material-ui/core';
import { Button, Autocomplete, TextField } from '@avangard/ui/core';

import { CommonHelper } from '@helpers';
import { useEnqueueStacks } from '@modules/layout/hooks';
import { FormStyled } from '@modules/layout/styled';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { useCurrentLap } from '@modules/lap/hooks';
import { useSchools } from '@modules/schools/hooks';
import { GetStudentsImportTemplateUrlQuery } from '@modules/students/graphql';

import type { SchoolEntity } from '@modules/schools/entities';
import type {
    GetStudentsImportTemplateUrlQueryType,
    GetStudentsImportTemplateUrlQueryVariables,
} from '@modules/types/graphql';

type StudentsImportTemplateFormValues = {
    school: SchoolEntity | null;
    type: string;
};

type StudentCreateFormProps = {
    onCancel: () => void;
};

const StyledForm = styled(Form)`
    width: 100%;

    > * {
        &:not(:last-of-type) {
            margin-bottom: 24px;
        }
    }
`;

const FormRowSubmit = styled(FormStyled.FormRowSubmit)`
    justify-content: flex-end;
`;

const templateTypes = {
    single: 'students:blocks.template_types.single',
    individual: 'students:blocks.template_types.individual',
};

const templatesTypesOptions = Object.entries(templateTypes);

const StudentsTemplateForm = (props: StudentCreateFormProps) => {
    const { onCancel } = props;

    const { t } = useTranslation(['common', 'errors', 'students']);

    const { enqueueError } = useEnqueueStacks();

    const { currentLap, loading: currentLapLoading } = useCurrentLap();

    const {
        schools,
        count: schoolsCount,
        loading: schoolsLoading,
    } = useSchools({
        variables: {
            args: {
                page: { all: true },
            },
        },
    });

    const [getStudentsImportTemplate, { loading: getStudentsImportTemplateLoading }] = useLazyQuery<
        GetStudentsImportTemplateUrlQueryType,
        GetStudentsImportTemplateUrlQueryVariables
    >(GetStudentsImportTemplateUrlQuery, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            const url = data?.getStudentsImportTemplateUrl;

            if (url) {
                CommonHelper.downloadUrl(url);
                onCancel();
            } else {
                enqueueError(t('students:notifiers.export_students_template.error'));
            }
        },
    });

    const getOptionLabel =
        (field: string) =>
        (option: any | null): string =>
            option?.[field] || '-';

    const hasCurrentLap = currentLap.exists();

    return (
        <ExtendedFormik<StudentsImportTemplateFormValues>
            validationSchema={yup.object({
                type: yup.string().required(t('errors:validations.required') ?? ''),
                school: yup.mixed().when('type', {
                    is: 'individual',
                    then: yup.mixed().required(t('errors:validations.required') ?? ''),
                }),
            })}
            initialValues={{
                type: 'single',
                school: null,
            }}
            onSubmit={values => {
                try {
                    const variables: Record<string, any> = {
                        schoolId: undefined,
                    };

                    const templateTypeIndividual = values.type === 'individual';

                    if (templateTypeIndividual && values.school) {
                        variables.schoolId = values.school.id;
                    }

                    getStudentsImportTemplate({ variables });
                } catch (e) {
                    console.log(e);
                    throw e;
                }
            }}
        >
            {formikProps => {
                const { values, errors, setFieldValue, handleChange, handleBlur } = formikProps;

                const handleChangeAutocomplete =
                    (field: string) => (_: React.ChangeEvent<{}>, value: SchoolEntity | null) =>
                        setFieldValue(field, value);

                const templateTypeSingle = values.type === 'single';

                return (
                    <StyledForm>
                        <TextField
                            required
                            select
                            fullWidth
                            id='type'
                            name='type'
                            label={t('students:form.labels.template_type')}
                            placeholder={t('students:form.placeholder.choose_template_type') ?? ''}
                            value={values.type}
                            error={!!errors.type}
                            helperText={errors.type}
                            disabled={!hasCurrentLap || currentLapLoading}
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            {templatesTypesOptions.map(([type, translate]) => (
                                <MenuItem key={type} value={type}>
                                    {t(translate)}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Autocomplete<SchoolEntity | null, false, false, false>
                            id='school'
                            label={t('common:forms.labels.school_short_name') ?? ''}
                            placeholder={
                                t('common:forms.placeholder.choose_school_short_name') ?? ''
                            }
                            required={values.type === 'individual'}
                            options={schools}
                            value={values.school}
                            error={!!errors.school}
                            helperText={errors.school}
                            disabled={templateTypeSingle || schoolsLoading || schoolsCount === 0}
                            onChange={handleChangeAutocomplete('school')}
                            getOptionLabel={getOptionLabel('shortName')}
                        />

                        <FormRowSubmit>
                            <Button
                                variant='outlined'
                                disabled={getStudentsImportTemplateLoading}
                                onClick={onCancel}
                            >
                                {t('common:actions.cancel')}
                            </Button>

                            <Button type='submit' loading={getStudentsImportTemplateLoading}>
                                {t('common:actions.download_template')}
                            </Button>
                        </FormRowSubmit>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { StudentsTemplateForm };
